<script setup lang="ts">
    import { computed, onMounted, ref, shallowRef, type Component } from 'vue'
    import { useTheme } from 'composables'
    import { useI18n } from 'vue-i18n'
    import PkCode from './chat/PkCode.vue'

    const { t: $t } = useI18n({ useScope: 'global' })

    const props = defineProps<{
        language?: string
        label?: string
        placeholder?: string
        hintLabel?: string
        readonly?: boolean
        disabled?: boolean
    }>()

    const modelValue = defineModel<string | undefined>({ default: undefined })
    const isDialogVisible = ref(false)
    const localValue = ref('')

    const { isDarkTheme } = useTheme()
    const monacoTheme = computed(() => (isDarkTheme.value ? 'vs-dark' : 'vs'))

    const editorOptions = {
        minimap: { enabled: false },
        automaticLayout: true,
        fontSize: 12,
        scrollBeyondLastLine: false,
        wordWrap: 'on' as const,
        readOnly: false,
    }

    const CodeEditor = shallowRef<Component | null>(null)

    onMounted(async () => {
        const { CodeEditor: Editor } = await import('monaco-editor-vue3')
        CodeEditor.value = Editor
    })

    const isInteractive = computed(() => !props.readonly && !props.disabled)

    function onClickEdit() {
        if (!isInteractive.value) {
            return
        }
        localValue.value = modelValue.value ?? ''
        isDialogVisible.value = true
    }

    function onClickSave() {
        modelValue.value = localValue.value || undefined
        isDialogVisible.value = false
    }

    const hasValue = computed(
        () => modelValue.value != null && modelValue.value.trim().length > 0,
    )
</script>

<template>
    <div
        class="pk-editor-code"
        :class="{
            'pk-editor-code--disabled': disabled,
            'pk-editor-code--readonly': readonly,
            'pk-editor-code--dirty': hasValue,
        }">
        <span v-if="label" class="pk-editor-code__label">
            {{ label }}
        </span>
        <!-- A focusable box like the prompt editor's: with code inside the
         pencil is not rendered, so the keyboard needs the box itself.
         Enter opens the editor, Space keeps scrolling the preview -->
        <div
            class="pk-editor-code__input"
            :tabindex="isInteractive ? 0 : undefined"
            :aria-label="isInteractive ? label || undefined : undefined"
            :title="$t('action.edit')"
            @click.stop="onClickEdit"
            @keydown.enter.self.prevent="onClickEdit">
            <div v-if="hasValue" class="pk-editor-code__preview">
                <PkCode :code="modelValue" :language="language ?? 'css'" />
            </div>
            <template v-else>
                <span v-if="placeholder" class="pk-editor-code__placeholder">
                    {{ placeholder }}
                </span>
                <VvButton
                    v-if="!readonly"
                    class="pk-editor-code__edit"
                    icon="ri:edit-line"
                    modifiers="action-small"
                    :disabled
                    :title="$t('action.edit')"
                    @click="onClickEdit" />
            </template>
        </div>
        <small v-if="$slots.hint || hintLabel" class="pk-editor-code__hint">
            <slot name="hint">{{ hintLabel }}</slot>
        </small>
        <VvDialog
            v-model="isDialogVisible"
            :title="label || $t('action.edit')"
            modifiers="large"
            keep-open>
            <div class="pk-editor-code__editor">
                <component
                    :is="CodeEditor"
                    v-if="CodeEditor"
                    v-model:value="localValue"
                    :language="language ?? 'css'"
                    :theme="monacoTheme"
                    :options="editorOptions"
                    width="100%"
                    height="100%" />
            </div>
            <template #footer>
                <VvButtonGroup>
                    <VvButton
                        modifiers="secondary"
                        :label="$t('action.cancel')"
                        @click="isDialogVisible = false" />
                    <VvButton :label="$t('action.save')" @click="onClickSave" />
                </VvButtonGroup>
            </template>
        </VvDialog>
    </div>
</template>

<style lang="scss">
    @layer volver.components {
        .pk-editor-code {
            position: relative;
            margin-bottom: var(--input-gap);
            overflow: hidden;

            &.mb-0 {
                margin-bottom: 0;
            }

            pre code.hljs {
                padding: 0;
            }

            .monaco-editor {
                --vscode-editor-foreground: transparent;
                --vscode-editor-background: transparent;
                --vscode-editorGutter-background: var(--color-surface-1);

                .main {
                    background-color: transparent;
                }

                .view-overlays {
                    .current-line-exact {
                        border: none;
                        background-color: var(--color-surface-1);
                    }
                }

                .decorationsOverviewRuler {
                    background-color: var(--color-surface);
                }
            }

            &__label {
                color: var(--input-label-color);
                display: var(--input-label-display);
                font-size: var(--input-label-font-size);
                font-weight: var(--input-label-font-weight);
                margin-bottom: var(--input-label-gap);
            }

            // The outlined field of the design system, drawn by hand: the
            // box previews the code and opens the editor on click
            &__input {
                position: relative;
                display: flex;
                flex-direction: column;
                justify-content: start;
                min-width: 0;
                background-color: var(--input-background-color);
                font-size: var(--input-font-size);

                // Room on the right for the 24px pencil plus the padding on
                // both of its sides
                padding: var(--input-spacing-top)
                    calc(var(--input-spacing-right) * 2 + var(--spacing-24))
                    var(--input-spacing-bottom) var(--input-spacing-left);
                min-height: var(--input-min-height);
                max-height: var(--spacing-192);
                line-height: var(--input-line-height);
                overflow: auto;
                cursor: pointer;
                border: var(--spacing-px) solid var(--input-border-color);
                border-radius: var(--input-border-radius);
                transition: var(--input-transition);

                &:hover {
                    border-color: var(--input-border-color-hover);
                }

                &:focus-visible {
                    outline: none;
                    border-color: var(--input-focus-color);
                    box-shadow: var(--input-focus-ring);
                }
            }

            &__preview {
                max-height: var(--spacing-96);
            }

            &__placeholder {
                color: var(--input-placeholder-color);
                margin-block: auto;
            }

            // An `action-small` squared to the 24px of the text line and set
            // in by the field padding, like the text
            &__edit {
                position: absolute;
                top: var(--input-spacing-top);
                right: var(--input-spacing-right);
                inline-size: var(--spacing-24);
                block-size: var(--spacing-24);
                padding: 0;
                justify-content: center;
                border-radius: var(--rounded);
            }

            &__hint {
                display: var(--input-hint-display);
                font-size: var(--input-hint-font-size);
                color: var(--input-hint-color);
                padding-top: var(--input-hint-gap);
                padding-inline: var(--spacing-xs);
                overflow: var(--input-hint-overflow);
            }

            &__editor {
                height: 400px;
                overflow: hidden;
                margin: calc(-1 * var(--spacing-sm));
            }

            &--readonly {
                .pk-editor-code__input {
                    cursor: default;
                    padding-right: var(--input-spacing-right);

                    &:hover {
                        border-color: var(--input-border-color);
                    }
                }
            }

            &--disabled {
                .pk-editor-code__input {
                    cursor: not-allowed;
                    background-color: var(--input-disabled-background-color);
                    color: var(--input-disabled-color);

                    &,
                    &:hover {
                        border-color: var(--input-disabled-border-color);
                    }
                }
            }

            &--dirty {
                .pk-editor-code__input {
                    padding-right: 0;
                }
            }
        }
    }
</style>
